// mailinator_clone/frontend/src/layouts/Layout.js
import React, { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Paper,
  Autocomplete,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import HomeIcon from '@mui/icons-material/Home';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import DataObjectIcon from '@mui/icons-material/DataObject';
import MapIcon from '@mui/icons-material/Map';
import ArticleIcon from '@mui/icons-material/Article';
import { ThemeContext } from '../styles/ThemeContext';
import Sidebar from './Sidebar';

import TextFieldsIcon from '@mui/icons-material/TextFields';
import CodeIcon from '@mui/icons-material/Code';
import PaletteIcon from '@mui/icons-material/Palette';
import ApiIcon from '@mui/icons-material/Api';

const navCategories = [
  {
    title: 'Workspace Tools',
    links: [
      { label: 'JSON Tools', path: '/json-tools', icon: <DataObjectIcon /> },
      { label: 'API Tools', path: '/api-tools', icon: <ApiIcon /> },
      { label: 'Spatial Tools', path: '/spatial-tools-app', icon: <MapIcon /> },
      { label: 'Text Tools', path: '/text-tools-app', icon: <TextFieldsIcon /> },
      { label: 'Dev Tools', path: '/dev-tools', icon: <CodeIcon /> },
      { label: 'Color/Image Tools', path: '/color-image-tools', icon: <PaletteIcon /> },
      { label: 'Mailinator', path: '/mailinator', icon: <EmailIcon /> },
    ]
  },
  {
    title: 'Company',
    links: [
      { label: 'Home', path: '/home', icon: <HomeIcon /> },
      { label: 'Blog', path: '/blog', icon: <ArticleIcon /> },
      { label: 'About', path: '/about', icon: <InfoIcon /> },
      { label: 'Contact', path: '/contact', icon: <ContactMailIcon /> },
    ]
  }
];


const drawerWidth = 220;
const collapsedDrawerWidth = 70;

const allTools = navCategories.flatMap(category =>
  category.links.map(link => ({ ...link, category: category.title }))
);

function Layout({ minimal = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme } = useContext(ThemeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const ignorePaths = ['/', '/home', '/about', '/contact', '/blog'];
    // Only track if it's not a generic page
    if (!ignorePaths.includes(location.pathname)) {
      try {
        const recent = JSON.parse(localStorage.getItem('umbrella_recent_tools') || '[]');
        const newRecent = [location.pathname, ...recent.filter(p => p !== location.pathname)].slice(0, 3);
        localStorage.setItem('umbrella_recent_tools', JSON.stringify(newRecent));
      } catch (e) {
        console.error('Failed to save recent tool', e);
      }
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };


  const currentDrawerWidth = 260;

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh / 0.9)', bgcolor: 'background.default' }}>
      <Sidebar
        navCategories={navCategories}
        drawerWidth={currentDrawerWidth}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: minimal ? 0 : 3, // No padding in minimal mode
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* ------------------ HEADER ------------------ */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: '100%',
            ml: 0,
            backgroundColor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ color: 'text.primary', mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
              <Link to="/home" style={{ display: 'flex', alignItems: 'center' }}>
                <img src="/logo-full-s.png" alt="logo" style={{ height: '40px' }} />
              </Link>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', maxWidth: 500 }}>
                <Autocomplete
                  options={allTools}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => option.label}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      navigate(newValue.path);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search UmbrellaCo tools..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ pl: 1 }}>
                              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          paddingLeft: '8px'
                        },
                        '& fieldset': { border: 'none' }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                <IconButton onClick={toggleTheme} color="inherit" sx={{ color: 'text.primary' }}>
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <Avatar alt="User" sx={{ width: 32, height: 32, ml: 1, bgcolor: 'primary.main', fontWeight: 700 }}>
                U
              </Avatar>
            </Box>
          </Toolbar>
        </AppBar>

        {/* ------------------ MAIN CONTENT ------------------ */}
        {minimal ? (
          <Box sx={{ width: '100%', flexGrow: 1, mt: '64px', height: 'calc(100vh / 0.9 - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Outlet />
          </Box>
        ) : (
          <Container
            component="main"
            disableGutters
            maxWidth="xl"
            sx={{
              flexGrow: 1,
              py: isMobile ? 1 : 2,
              px: isMobile ? 1 : 2,
              display: 'flex',
              justifyContent: 'center',
              mt: '10vh', // Add margin top to push content below the fixed AppBar
            }}
          >
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                width: '100%',
                minHeight: '75vh',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'auto',
              }}
            >
              <Outlet />
            </Paper>
          </Container>
        )}

        {/* ------------------ FOOTER ------------------ */}
        {!minimal && (
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 2,
              mt: 'auto',
              bgcolor: 'background.paper',
              borderTop: `1px solid ${theme.palette.divider}`,
              textAlign: 'center',
              flexShrink: 0,
              position: 'relative'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              &copy; {new Date().getFullYear()} UmbrellaCo. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.95, color: 'text.secondary' }}>
              Built with{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                ☕ x ∞ and 😵‍💫
              </Box>
              , and&nbsp;
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                zero regrets. (Okay maybe one... or ten 😬)
              </Box>
              .
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Layout;
